/* A recorded run's trace: read from the API and, while the run is live, kept
   current from the events socket (`useRunTrace`) — then drawn by TraceDetail,
   from the tree `recordedSpans` makes of it. */
import { useEffect, useMemo, useState } from "react";

import { useRunTrace } from "@/shared/api/traces";
import TraceDetail from "./TraceDetail";
import { recordedSpans } from "./recorded";

import type { ComponentProps } from "react";

/** How often a live trace redraws, so a step still running is seen to grow. */
const TICK_MS = 1_000;

function useNow(ticking: boolean): number {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!ticking) return;
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, [ticking]);
  return now;
}

export default function RecordedTrace(props: Omit<ComponentProps<typeof TraceDetail>, "root" | "votes">) {
  const { run, agent } = props;
  const live = run.status === "running" || run.status === "queued";
  const trace = useRunTrace(run.id, live);
  const now = useNow(live);
  const root = useMemo(() => trace.data && recordedSpans(trace.data, run, agent, now), [trace.data, run, agent, now]);
  if (!root) {
    return (
      <div className="flex-1 min-w-0 flex items-center justify-center text-[13px] text-[var(--text-tertiary)]">
        {trace.isError ? "This trace could not be loaded." : "Loading trace…"}
      </div>
    );
  }
  return <TraceDetail {...props} root={root} votes={trace.data?.votes ?? []} />;
}
